'use client';

import { useEffect, useRef, useState } from 'react';
import {
  Info,
  MousePointerClick,
  X,
} from 'lucide-react';
import { useI18n } from '@/contexts/I18nContext';
import { useEditorStore } from '@/lib/editor/editorStore';
import {
  MAX_TRANSITION_SEC,
  MIN_TRANSITION_SEC,
  TRANSITION_LABELS,
  TRANSITION_TYPES,
  effectiveTransitionDuration,
  renderStartTimes,
} from '@/lib/editor/transitions';
import {
  effectiveLayerTransitionDuration,
  LAYER_TRANSITION_TYPES,
} from '@/lib/editor/layerTransitions';
import type { EditorOverlayLayer, EditorTransition, TransitionType } from '@/types/editor';

const DEFAULT_TRANSITION_DURATION_SEC = 0.5;
const DEFAULT_TRANSITION_TYPE: TransitionType = 'dissolve';

/**
 * Left "Переходы" tool panel — Phase 3A task 4.
 *
 * The UI attaches a transition to the selected clip's OUTGOING (right) edge.
 * The render contract still stores it as `transitionIn` on the next clip,
 * which keeps ffmpeg/preview timing unchanged while presenting the familiar
 * "transition at the end of this clip" editing model.
 */
export function TransitionsPanel() {
  const { t } = useI18n();
  const clips = useEditorStore((s) => s.clips);
  const layers = useEditorStore((s) => s.layers);
  const selectedClipId = useEditorStore((s) => s.selectedClipId);
  const selectedLayerId = useEditorStore((s) => s.selectedLayerId);
  const setTransition = useEditorStore((s) => s.setTransition);
  const removeTransition = useEditorStore((s) => s.removeTransition);
  const updateLayer = useEditorStore((s) => s.updateLayer);
  const setCurrentTime = useEditorStore((s) => s.setCurrentTime);
  const setIsPlaying = useEditorStore((s) => s.setIsPlaying);
  const previewTimerRef = useRef<number | null>(null);
  // Tracks component mount state so an in-flight preview timeout can bail
  // instead of firing setCurrentTime/setIsPlaying against an unmounted panel.
  const mountedRef = useRef(true);
  const [layerEdge, setLayerEdge] = useState<'in' | 'out'>('in');

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (previewTimerRef.current !== null) {
        window.clearTimeout(previewTimerRef.current);
        previewTimerRef.current = null;
        setIsPlaying(false);
      }
    };
  }, [setIsPlaying]);

  const selectedIndex = clips.findIndex((c) => c.id === selectedClipId);
  const selectedClip = selectedIndex >= 0 ? clips[selectedIndex] : undefined;
  const selectedLayer = layers.find((layer) => layer.id === selectedLayerId);

  let body: React.ReactNode;

  if (selectedLayer && selectedLayer.type !== 'effect') {
    body = (
      <LayerTransitionsEditor
        layer={selectedLayer}
        edge={layerEdge}
        onEdgeChange={setLayerEdge}
        onUpdate={(patch) => updateLayer(selectedLayer.id, patch)}
        onPreview={(transition, edge) => {
          const previewDuration = effectiveLayerTransitionDuration(selectedLayer, transition);
          const previewStart = edge === 'in'
            ? selectedLayer.start
            : Math.max(selectedLayer.start, selectedLayer.end - previewDuration);
          // Capture the target layer at schedule time so the deferred
          // stop-preview step below can detect a selection change and bail.
          const scheduledLayerId = selectedLayer.id;
          setCurrentTime(previewStart);
          setIsPlaying(true);
          if (previewTimerRef.current !== null) window.clearTimeout(previewTimerRef.current);
          previewTimerRef.current = window.setTimeout(() => {
            previewTimerRef.current = null;
            if (!mountedRef.current) return;
            if (useEditorStore.getState().selectedLayerId !== scheduledLayerId) return;
            setIsPlaying(false);
            setCurrentTime(edge === 'in' ? selectedLayer.start + previewDuration : selectedLayer.end);
          }, Math.max(180, (previewDuration + 0.08) * 1000));
        }}
      />
    );
  } else if (!selectedClip) {
    body = <EmptyHint>Выберите видеоклип или текст на таймлайне</EmptyHint>;
  } else if (selectedIndex === clips.length - 1) {
    body = <EmptyHint>После этого клипа пока нет видео — добавьте следующий клип</EmptyHint>;
  } else {
    const nextClip = clips[selectedIndex + 1];
    const transitionClipId = nextClip.id;
    const transitionIn = nextClip.transitionIn;
    const currentType = transitionIn?.type;
    const currentDuration = transitionIn?.durationSec ?? DEFAULT_TRANSITION_DURATION_SEC;
    const previewTransition = (type: TransitionType) => {
      const requestedDuration = transitionIn?.durationSec ?? DEFAULT_TRANSITION_DURATION_SEC;
      setTransition(transitionClipId, { type, durationSec: requestedDuration });

      const nextState = useEditorStore.getState();
      const previewStart = renderStartTimes(nextState.clips)[selectedIndex + 1] ?? 0;
      const previewDuration = effectiveTransitionDuration(
        selectedClip.duration,
        nextClip.duration,
        nextState.clips[selectedIndex + 1]?.transitionIn
      );
      // Capture the target clip at schedule time so the deferred
      // stop-preview step below can detect a selection change and bail.
      const scheduledClipId = selectedClip.id;
      setCurrentTime(Math.max(0, previewStart - 0.06));
      setIsPlaying(true);

      if (previewTimerRef.current !== null) window.clearTimeout(previewTimerRef.current);
      previewTimerRef.current = window.setTimeout(() => {
        previewTimerRef.current = null;
        if (!mountedRef.current) return;
        if (useEditorStore.getState().selectedClipId !== scheduledClipId) return;
        setIsPlaying(false);
        setCurrentTime(previewStart + previewDuration / 2);
      }, (previewDuration + 0.12) * 1000);
    };

    body = (
      <>
        <div className="rounded-lg border border-white/10 bg-black/15 px-2.5 py-2">
          <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-white/35">Стык</p>
          <p className="mt-1 truncate text-xs text-white/70">
            {selectedClip.title} <span className="text-brand-300">→</span> {nextClip.title}
          </p>
        </div>
        <p className="mt-3 text-xs leading-5 text-white/60">Выберите эффект перехода</p>

        <TransitionGrid types={TRANSITION_TYPES} currentType={currentType} onSelect={previewTransition} />

        <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-2.5">
          <div className="mb-2 flex items-center justify-between gap-2">
            <p className="text-[10px] font-black uppercase tracking-[0.14em] text-white/50">Длительность</p>
            <span className="rounded-full bg-white/5 px-2.5 py-1 text-[11px] font-black tabular-nums text-white/60">
              {currentDuration.toFixed(1)}с
            </span>
          </div>
          <input
            type="range"
            min={MIN_TRANSITION_SEC}
            max={MAX_TRANSITION_SEC}
            step={0.1}
            value={currentDuration}
            disabled={!currentType}
            onChange={(e) =>
              setTransition(transitionClipId, {
                type: currentType ?? DEFAULT_TRANSITION_TYPE,
                durationSec: Number(e.target.value),
              })
            }
            className="w-full accent-brand-500 disabled:opacity-40"
          />
        </div>

        {transitionIn && (
          <button
            type="button"
            onClick={() => removeTransition(transitionClipId)}
            className="mt-3 inline-flex h-9 w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 text-xs font-black text-white shadow-sm transition hover:border-red-400/60 hover:text-red-300"
          >
            <X className="h-3.5 w-3.5" />
            Убрать переход
          </button>
        )}

        <div className="mt-4 flex items-start gap-2 rounded-xl bg-white/5 px-2.5 py-2.5">
          <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-white/40" />
          <p className="text-[11px] leading-5 text-white/50">
            Переход стоит на правом краю выбранного клипа. Превью и экспорт используют одно и то же окно эффекта.
          </p>
        </div>
      </>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-[#121214] text-white">
      <div className="flex h-10 shrink-0 items-center border-b border-white/10 px-3.5">
        <p className="text-sm font-semibold">{t('studio.editor.toolTransitions')}</p>
      </div>

      <div data-lenis-prevent className="min-h-0 flex-1 overflow-y-auto p-3">
        {body}
      </div>
    </div>
  );
}

interface LayerTransitionsEditorProps {
  layer: EditorOverlayLayer;
  edge: 'in' | 'out';
  onEdgeChange: (edge: 'in' | 'out') => void;
  onUpdate: (patch: Partial<EditorOverlayLayer>) => void;
  onPreview: (transition: EditorTransition, edge: 'in' | 'out') => void;
}

function LayerTransitionsEditor({
  layer,
  edge,
  onEdgeChange,
  onUpdate,
  onPreview,
}: LayerTransitionsEditorProps) {
  const field = edge === 'in' ? 'transitionIn' : 'transitionOut';
  const currentTransition = layer[field];
  const currentType = currentTransition?.type;
  const currentDuration = currentTransition?.durationSec ?? DEFAULT_TRANSITION_DURATION_SEC;

  const chooseTransition = (type: TransitionType) => {
    const transition = { type, durationSec: currentDuration };
    onUpdate({ [field]: transition });
    onPreview(transition, edge);
  };

  return (
    <>
      <div className="rounded-lg border border-white/10 bg-black/15 px-2.5 py-2">
        <p className="text-[10px] font-bold uppercase tracking-[0.12em] text-white/35">Выбранный слой</p>
        <p className="mt-1 truncate text-xs font-semibold text-white/75">{layer.text || layer.label}</p>
      </div>

      <div className="mt-3 grid grid-cols-2 rounded-xl border border-white/10 bg-white/[0.035] p-1">
        {(['in', 'out'] as const).map((item) => (
          <button
            key={item}
            type="button"
            onClick={() => onEdgeChange(item)}
            className={`h-8 rounded-lg text-xs font-bold transition ${
              edge === item ? 'bg-brand-600 text-white shadow-sm' : 'text-white/50 hover:text-white'
            }`}
          >
            {item === 'in' ? 'Появление' : 'Исчезновение'}
          </button>
        ))}
      </div>

      <p className="mt-3 text-xs leading-5 text-white/60">
        {edge === 'in' ? 'Как слой появляется в кадре' : 'Как слой уходит из кадра'}
      </p>
      <TransitionGrid
        types={LAYER_TRANSITION_TYPES}
        currentType={currentType}
        onSelect={chooseTransition}
      />

      <div className="mt-4 rounded-xl border border-white/10 bg-white/5 p-2.5">
        <div className="mb-2 flex items-center justify-between gap-2">
          <p className="text-[10px] font-black uppercase tracking-[0.14em] text-white/50">Длительность</p>
          <span className="rounded-full bg-white/5 px-2.5 py-1 text-[11px] font-black tabular-nums text-white/60">
            {currentDuration.toFixed(1)}с
          </span>
        </div>
        <input
          type="range"
          min={MIN_TRANSITION_SEC}
          max={MAX_TRANSITION_SEC}
          step={0.1}
          value={currentDuration}
          disabled={!currentTransition}
          onChange={(event) => {
            if (!currentTransition) return;
            onUpdate({
              [field]: { ...currentTransition, durationSec: Number(event.target.value) },
            });
          }}
          className="w-full accent-brand-500 disabled:opacity-40"
        />
      </div>

      {currentTransition && (
        <button
          type="button"
          onClick={() => onUpdate({ [field]: undefined })}
          className="mt-3 inline-flex h-9 w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/5 text-xs font-black text-white shadow-sm transition hover:border-red-400/60 hover:text-red-300"
        >
          <X className="h-3.5 w-3.5" />
          Убрать {edge === 'in' ? 'появление' : 'исчезновение'}
        </button>
      )}

      <div className="mt-4 flex items-start gap-2 rounded-xl bg-white/5 px-2.5 py-2.5">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-white/40" />
        <p className="text-[11px] leading-5 text-white/50">
          Анимация привязана к краям слоя на таймлайне и сохраняется при экспорте.
        </p>
      </div>
    </>
  );
}

function TransitionGrid({
  types,
  currentType,
  onSelect,
}: {
  types: readonly TransitionType[];
  currentType?: TransitionType;
  onSelect: (type: TransitionType) => void;
}) {
  return (
    <div className="mt-3 grid grid-cols-3 gap-2">
      {types.map((type) => {
        const active = type === currentType;
        return (
          <button
            key={type}
            type="button"
            data-active={active ? 'true' : 'false'}
            onClick={() => onSelect(type)}
            className={`group flex flex-col items-center gap-1.5 rounded-xl border p-1.5 text-center transition ${
              active
                ? 'border-brand-400 bg-brand-600/30 text-white ring-2 ring-brand-400/70'
                : 'border-white/10 bg-white/5 text-white/70 hover:border-brand-500 hover:text-brand-300'
            }`}
          >
            <TransitionMiniPreview type={type} />
            <span className="min-h-6 text-[10px] font-bold leading-3">{TRANSITION_LABELS[type]}</span>
          </button>
        );
      })}
    </div>
  );
}

function TransitionMiniPreview({ type }: { type: TransitionType }) {
  return (
    <span
      data-transition-type={type}
      className="transition-mini-preview relative block aspect-video w-full overflow-hidden rounded-lg border border-white/10 bg-[#09070e]"
      aria-hidden="true"
    >
      <span className="transition-mini-scene transition-mini-a absolute inset-0 bg-[radial-gradient(circle_at_30%_35%,#ffd166_0_11%,transparent_12%),linear-gradient(145deg,#ef476f,#7b2cbf)]" />
      <span className="transition-mini-scene transition-mini-b absolute inset-0 bg-[radial-gradient(circle_at_68%_36%,#b8f2e6_0_10%,transparent_11%),linear-gradient(145deg,#00b4d8,#023e8a)]" />
      <span className="pointer-events-none absolute inset-x-0 bottom-0 h-1/3 bg-gradient-to-t from-black/35 to-transparent" />
    </span>
  );
}

function EmptyHint({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-white/15 px-3 py-6 text-center">
      <MousePointerClick className="h-5 w-5 text-white/30" />
      <p className="text-xs leading-5 text-white/50">{children}</p>
    </div>
  );
}
